import { useState } from 'react'
import { useDefinirEquipeInterna } from '../../api/queries/repositorios'
import { Botao } from '../../components/Botao'
import { CampoDeCaixa, CampoDeTextoLongo } from '../../components/campos'
import { Dialogo } from '../../components/Dialogo'
import { AvisoDeErro } from '../../components/Estados'
import { Explicacao } from '../../components/Explicacao'
import { TEXTOS } from '../../lib/glossario'

interface Props {
  autor: { id: number; nome: string; email: string; equipeInterna: boolean }
  aoFechar: () => void
}

/**
 * Marcar ou desmarcar alguém como equipe interna — o ato do fiscal sobre o casamento com o Diário.
 *
 * O casamento automático erra nos dois sentidos (homônimo que casa, e-mail novo que não casa). O
 * que se fixa aqui fica com o nome de quem fixou, e a sincronização não sobrescreve mais.
 *
 * A caixa nasce com a INTENÇÃO do botão que abriu o diálogo, e não com o estado atual: quem clicou
 * em "Tirar da equipe interna" e salvou sem mexer na caixa estava, na versão anterior, fixando a
 * pessoa como equipe interna por decisão de PESSOA — e a sincronização nunca mais a soltava.
 * Pelo mesmo motivo, Salvar fica travado enquanto a caixa diz o que o autor já é: gravar o mesmo
 * valor trocaria a origem do Diário por uma decisão que ninguém tomou.
 *
 * @param props o autor e o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDeEquipeInterna({ autor, aoFechar }: Props) {
  const t = TEXTOS.repositorios.autores.dialogo
  const intencao = autor.equipeInterna ? t.tirar : t.marcar
  const [equipeInterna, setEquipeInterna] = useState(!autor.equipeInterna)
  const [observacao, setObservacao] = useState('')
  const definir = useDefinirEquipeInterna()
  const semMudanca = equipeInterna === autor.equipeInterna

  function salvar() {
    definir.mutate({ id: autor.id, equipeInterna, observacao }, { onSuccess: aoFechar })
  }

  return (
    <Dialogo
      titulo={intencao.titulo}
      descricao={`${autor.nome} · ${autor.email}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao aparencia="secundario" onClick={aoFechar} disabled={definir.isPending}>
            {t.cancelar}
          </Botao>
          <Botao aparencia="primario" onClick={salvar} disabled={definir.isPending || semMudanca}>
            {t.salvar}
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <p className="flex items-start gap-1 text-sm text-slate-600">
          <span>{intencao.ajuda}</span>
          <Explicacao termo="equipeInterna" />
        </p>
        <CampoDeCaixa rotulo={t.caixa} checked={equipeInterna} onChange={(evento) => setEquipeInterna(evento.target.checked)} />
        <CampoDeTextoLongo
          rotulo={t.observacao}
          ajuda={t.observacaoAjuda}
          value={observacao}
          onChange={(evento) => setObservacao(evento.target.value)}
          rows={3}
          maxLength={1000}
        />
        {definir.isError && <AvisoDeErro erro={definir.error} />}
      </div>
    </Dialogo>
  )
}
